import React, { useState, useEffect, useMemo } from "react";
import {
  Cat,
  Fish,
  Syringe,
  Sparkles,
  Plus,
  Wallet,
  TrendingDown,
  TrendingUp,
  Calendar,
  PieChart,
  Trash2,
  PawPrint,
  FileText,
  CloudLightning,
  Loader2,
  Clock,
  Filter,
  BarChart3, // 新增图标
} from "lucide-react";

// =================================================================
// 🔴 你的 Supabase 配置
// =================================================================
const supabaseUrl = "https://lsbmoiwrahejxacuawgk.supabase.co";
const supabaseKey = "sb_publishable_Wbd0Kr2xKBnTMW8lq_Yx8w_IuY8W1c5";

// 预设的消费分类
const CATEGORIES = {
  食物: {
    icon: <Fish />,
    subs: ["猫粮", "猫罐头", "猫零食", "冻干", "营养膏", "羊奶粉"],
  },
  用品: {
    icon: <Sparkles />,
    subs: ["猫砂", "猫抓板", "玩具", "猫窝", "食盆水碗", "牵引绳"],
  },
  医疗: { icon: <Syringe />, subs: ["疫苗", "驱虫", "绝育", "看病", "体检"] },
  其他: { icon: <Cat />, subs: ["洗护美容", "寄养", "保险", "杂项"] },
};

export default function App() {
  const [supabase, setSupabase] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [activeTab, setActiveTab] = useState("expense");
  const [showStats, setShowStats] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isLibLoaded, setIsLibLoaded] = useState(false);

  // --- 状态管理 ---
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [filterMonth, setFilterMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  // 表单状态
  const [amount, setAmount] = useState("");
  const [selectedMainCat, setSelectedMainCat] = useState("食物");
  const [selectedSubCat, setSelectedSubCat] = useState("猫粮");
  const [depositor, setDepositor] = useState("磊磊");
  const [spender, setSpender] = useState("磊磊");
  const [note, setNote] = useState("");

  // --- 1. 自动加载 Supabase 库 ---
  useEffect(() => {
    if (window.supabase) {
      setIsLibLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2";
    script.onload = () => setIsLibLoaded(true);
    script.onerror = () => alert("无法加载云数据库组件，请检查网络");
    document.body.appendChild(script);
  }, []);

  // --- 2. 初始化 Supabase ---
  useEffect(() => {
    if (isLibLoaded && supabaseUrl && supabaseKey) {
      try {
        // @ts-ignore
        const client = window.supabase.createClient(supabaseUrl, supabaseKey);
        setSupabase(client);
      } catch (error) {
        console.error("Supabase 初始化失败", error);
      }
    } else if (isLibLoaded && (!supabaseUrl || !supabaseKey)) {
      setConfigError(true);
      setLoading(false);
    }
  }, [isLibLoaded]);

  // --- 3. 获取数据与监听 ---
  useEffect(() => {
    if (!supabase) return;
    fetchData();
    const channel = supabase
      .channel("public:liuliu_bills")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "liuliu_bills" },
        () => {
          fetchData();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase]);

  const fetchData = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from("liuliu_bills")
        .select("*")
        .order("date", { ascending: false });
      if (!error) {
        setTransactions(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supabase) return;
    if (!amount || parseFloat(amount) <= 0) return;

    const selectedDateObj = new Date(date);
    const now = new Date();
    selectedDateObj.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds()
    );

    const newTransaction = {
      date: selectedDateObj.toISOString(),
      amount: parseFloat(amount),
      note: note.trim(),
      type: activeTab,
      category: activeTab === "expense" ? selectedMainCat : null,
      subcategory: activeTab === "expense" ? selectedSubCat : null,
      person: activeTab === "deposit" ? depositor : spender,
    };

    try {
      const { error } = await supabase
        .from("liuliu_bills")
        .insert([newTransaction]);
      if (error) {
        alert("保存失败: " + error.message);
      } else {
        setAmount("");
        setNote("");
        setDate(new Date().toISOString().split("T")[0]);
        showNotification(
          activeTab === "expense" ? `记账成功！${spender}辛苦啦` : "存钱成功！"
        );
        fetchData();
      }
    } catch (err) {
      alert("保存出错");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("确定删除吗？")) {
      await supabase.from("liuliu_bills").delete().eq("id", id);
      fetchData();
    }
  };

  // --- 统计计算 ---
  const totalBalance = useMemo(() => {
    const dep = transactions
      .filter((t) => t.type === "deposit")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const exp = transactions
      .filter((t) => t.type === "expense")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    return dep - exp;
  }, [transactions]);

  const totalLeilei = useMemo(
    () =>
      transactions
        .filter((t) => t.type === "deposit" && t.person === "磊磊")
        .reduce((s, t) => s + (Number(t.amount) || 0), 0),
    [transactions]
  );
  const totalTingting = useMemo(
    () =>
      transactions
        .filter((t) => t.type === "deposit" && t.person === "婷婷")
        .reduce((s, t) => s + (Number(t.amount) || 0), 0),
    [transactions]
  );

  // 根据选择的月份筛选
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => t.date && t.date.startsWith(filterMonth));
  }, [transactions, filterMonth]);

  // 月度统计
  const monthlyStats = useMemo(() => {
    const expense = filteredTransactions
      .filter((t) => t.type === "expense")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const deposit = filteredTransactions
      .filter((t) => t.type === "deposit")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const leileiSpent = filteredTransactions
      .filter((t) => t.type === "expense" && t.person === "磊磊")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const tingtingSpent = filteredTransactions
      .filter((t) => t.type === "expense" && t.person === "婷婷")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    return { expense, deposit, leileiSpent, tingtingSpent };
  }, [filteredTransactions]);

  // 分类统计
  const categoryStats = useMemo(() => {
    const stats = {};
    filteredTransactions
      .filter((t) => t.type === "expense")
      .forEach((t) => {
        if (!stats[t.category]) stats[t.category] = 0;
        stats[t.category] += Number(t.amount);
      });
    return Object.entries(stats).sort(([, a], [, b]) => b - a);
  }, [filteredTransactions]);

  // --- 新增：年度支出统计 ---
  const yearlyStats = useMemo(() => {
    // 从 filterMonth (例如 "2025-10") 中提取年份 "2025"
    const selectedYear = filterMonth.substring(0, 4);

    // 筛选出这一年的所有支出
    const yearExpenses = transactions.filter(
      (t) => t.type === "expense" && t.date && t.date.startsWith(selectedYear)
    );

    const total = yearExpenses.reduce((s, t) => s + (Number(t.amount) || 0), 0);

    // 年度个人支出
    const leilei = yearExpenses
      .filter((t) => t.person === "磊磊")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);
    const tingting = yearExpenses
      .filter((t) => t.person === "婷婷")
      .reduce((s, t) => s + (Number(t.amount) || 0), 0);

    return { year: selectedYear, total, leilei, tingting };
  }, [transactions, filterMonth]);

  if (!isLibLoaded)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        <Loader2 className="animate-spin mr-2" />
        加载中...
      </div>
    );
  if (configError) return <div className="p-10 text-center">请检查配置</div>;

  return (
    <div className="min-h-screen bg-[#FFF8F0] font-sans pb-10 text-gray-700">
      {notification && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-bounce">
          <div className="bg-gray-800 text-white px-4 py-2 rounded-full text-sm shadow-lg flex items-center gap-2">
            <CloudLightning size={14} className="text-yellow-400" />{" "}
            {notification}
          </div>
        </div>
      )}

      {/* 头部卡片 */}
      <div className="bg-orange-400 p-6 rounded-b-[40px] shadow-lg text-white relative overflow-hidden">
        <div className="absolute top-[-20px] right-[-20px] opacity-20">
          <PawPrint size={150} />
        </div>
        <div className="relative z-10 text-center">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-2 mb-1">
            <Cat className="fill-current" /> 六六消费账单
          </h1>
          <p className="text-orange-100 text-xs mb-6 flex items-center justify-center gap-1">
            {loading ? (
              <Loader2 className="animate-spin w-3 h-3" />
            ) : (
              <CloudLightning className="w-3 h-3" />
            )}
            {loading ? "正在同步..." : "云端已连接"}
          </p>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
            <p className="text-orange-100 text-xs uppercase tracking-wider mb-1">
              养猫基金总余额
            </p>
            <div className="text-4xl font-extrabold flex items-center justify-center gap-1">
              <span className="text-2xl">¥</span>{" "}
              {totalBalance.toLocaleString()}
            </div>
          </div>
          <div className="flex justify-between mt-4 px-2">
            <div className="text-center">
              <p className="text-xs text-orange-100">磊磊总存入</p>
              <p className="font-bold">¥{totalLeilei.toLocaleString()}</p>
            </div>
            <div className="w-px bg-orange-300/50 h-8 self-center"></div>
            <div className="text-center">
              <p className="text-xs text-orange-100">婷婷总存入</p>
              <p className="font-bold">¥{totalTingting.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 -mt-6">
        <div className="bg-white rounded-3xl shadow-xl p-5 mb-6 relative z-20">
          <div className="flex bg-gray-100 rounded-full p-1 mb-6">
            <button
              onClick={() => setActiveTab("expense")}
              className={`flex-1 py-2 rounded-full text-sm font-bold flex items-center gap-2 justify-center transition-all ${
                activeTab === "expense"
                  ? "bg-orange-500 text-white shadow-md"
                  : "text-gray-500"
              }`}
            >
              <TrendingDown size={16} /> 花费
            </button>
            <button
              onClick={() => setActiveTab("deposit")}
              className={`flex-1 py-2 rounded-full text-sm font-bold flex items-center gap-2 justify-center transition-all ${
                activeTab === "deposit"
                  ? "bg-green-500 text-white shadow-md"
                  : "text-gray-500"
              }`}
            >
              <Wallet size={16} /> 存钱
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-xl border border-gray-100">
              <Clock size={18} className="text-gray-400" />
              <span className="text-xs font-bold text-gray-500 whitespace-nowrap">
                日期:
              </span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-transparent text-sm font-bold text-gray-700 outline-none w-full"
              />
            </div>

            {/* 选择花钱人 */}
            {activeTab === "expense" && (
              <div className="grid grid-cols-2 gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setSpender("磊磊")}
                  className={`py-2 rounded-lg text-xs font-bold border transition-all flex items-center justify-center gap-1 ${
                    spender === "磊磊"
                      ? "bg-blue-50 border-blue-400 text-blue-600 ring-1 ring-blue-200"
                      : "border-gray-100 text-gray-400 bg-gray-50"
                  }`}
                >
                  👦 磊磊买的
                </button>
                <button
                  type="button"
                  onClick={() => setSpender("婷婷")}
                  className={`py-2 rounded-lg text-xs font-bold border transition-all flex items-center justify-center gap-1 ${
                    spender === "婷婷"
                      ? "bg-pink-50 border-pink-400 text-pink-600 ring-1 ring-pink-200"
                      : "border-gray-100 text-gray-400 bg-gray-50"
                  }`}
                >
                  👧 婷婷买的
                </button>
              </div>
            )}

            {activeTab === "expense" && (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  {Object.keys(CATEGORIES).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        setSelectedMainCat(cat);
                        setSelectedSubCat(CATEGORIES[cat].subs[0]);
                      }}
                      className={`flex flex-col items-center p-2 rounded-xl border ${
                        selectedMainCat === cat
                          ? "border-orange-500 bg-orange-50 text-orange-600"
                          : "border-gray-100 bg-white text-gray-400"
                      }`}
                    >
                      <div className="mb-1">{CATEGORIES[cat].icon}</div>
                      <span className="text-xs">{cat}</span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 bg-gray-50 p-3 rounded-xl">
                  {CATEGORIES[selectedMainCat].subs.map((sub) => (
                    <button
                      key={sub}
                      type="button"
                      onClick={() => setSelectedSubCat(sub)}
                      className={`px-3 py-1 rounded-full text-xs border ${
                        selectedSubCat === sub
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-white text-gray-500 border-gray-200"
                      }`}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "deposit" && (
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setDepositor("磊磊")}
                  className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 ${
                    depositor === "磊磊"
                      ? "border-blue-400 bg-blue-50 text-blue-600"
                      : "border-gray-100 text-gray-400"
                  }`}
                >
                  👦 磊磊存入
                </button>
                <button
                  type="button"
                  onClick={() => setDepositor("婷婷")}
                  className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 ${
                    depositor === "婷婷"
                      ? "border-pink-400 bg-pink-50 text-pink-600"
                      : "border-gray-100 text-gray-400"
                  }`}
                >
                  👧 婷婷存入
                </button>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1 ml-1">
                金额
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-400">
                  ¥
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-gray-50 border-none rounded-xl py-4 pl-10 pr-4 text-2xl font-bold text-gray-700 focus:ring-2 focus:ring-orange-300 outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1 ml-1">
                备注
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <FileText size={16} />
                </span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="写点什么..."
                  className="w-full bg-gray-50 border-none rounded-xl py-3 pl-10 pr-4 text-sm text-gray-700 focus:ring-2 focus:ring-orange-300 outline-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 text-white font-bold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform"
            >
              <Plus size={20} /> {activeTab === "expense" ? "记一笔" : "存进去"}
            </button>
          </form>
        </div>

        <div className="flex justify-between items-center px-2 mb-4">
          <h3 className="font-bold text-lg text-gray-700">
            {showStats ? "数据统计" : "最近记录"}
          </h3>
          <button
            onClick={() => setShowStats(!showStats)}
            className="text-sm text-orange-500 font-bold flex items-center gap-1 bg-white px-3 py-1 rounded-full shadow-sm"
          >
            {showStats ? <Calendar size={14} /> : <PieChart size={14} />}
            {showStats ? "看明细" : "看统计"}
          </button>
        </div>

        <div className="space-y-3 pb-10">
          {loading ? (
            <div className="text-center py-10 text-gray-400">
              <Loader2 className="animate-spin mx-auto mb-2" />
              加载中...
            </div>
          ) : showStats ? (
            <div className="space-y-4 animate-in fade-in">
              <div className="bg-white p-3 rounded-xl flex items-center justify-between shadow-sm border border-orange-100">
                <div className="flex items-center gap-2 text-orange-600 font-bold text-sm">
                  <Filter size={16} /> 筛选月份
                </div>
                <input
                  type="month"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="border-none bg-orange-50 text-orange-800 font-bold rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-orange-200"
                />
              </div>

              {/* 年度统计卡片 (新增) */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-2xl shadow-sm border border-indigo-100">
                <h4 className="text-sm font-bold text-indigo-800 mb-2 flex items-center gap-2">
                  <BarChart3 size={16} /> {yearlyStats.year} 年度支出
                </h4>
                <div className="text-2xl font-extrabold text-indigo-600 mb-2">
                  ¥{yearlyStats.total.toLocaleString()}
                </div>
                <div className="flex gap-4 text-xs">
                  <div className="text-blue-600">
                    👦 磊磊: ¥{yearlyStats.leilei.toLocaleString()}
                  </div>
                  <div className="text-pink-600">
                    👧 婷婷: ¥{yearlyStats.tingting.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm">
                <h4 className="text-sm font-bold text-gray-400 mb-3 uppercase">
                  本月收支 ({filterMonth})
                </h4>
                <div className="flex justify-between items-end border-b border-gray-50 pb-2 mb-2">
                  <span className="text-gray-500 text-sm">存入</span>
                  <span className="font-bold text-green-500 text-lg">
                    +¥{monthlyStats.deposit.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-end mb-3">
                  <span className="text-gray-500 text-sm">支出</span>
                  <span className="font-bold text-orange-500 text-lg">
                    -¥{monthlyStats.expense.toLocaleString()}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-bold text-gray-400 mb-2">
                    本月支出构成
                  </p>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-1 mb-1 text-xs font-bold text-blue-600">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>{" "}
                        磊磊
                      </div>
                      <div className="text-sm font-bold">
                        ¥{monthlyStats.leileiSpent.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1 mb-1 text-xs font-bold text-pink-600">
                        <span className="w-2 h-2 rounded-full bg-pink-500"></span>{" "}
                        婷婷
                      </div>
                      <div className="text-sm font-bold">
                        ¥{monthlyStats.tingtingSpent.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm">
                <h4 className="text-sm font-bold text-gray-400 mb-3 uppercase">
                  本月分类统计
                </h4>
                {categoryStats.length > 0 ? (
                  categoryStats.map(([cat, total]) => (
                    <div key={cat} className="space-y-1 mb-3 last:mb-0">
                      <div className="flex justify-between text-sm">
                        <span>{cat}</span>
                        <span className="font-bold">¥{total}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-400 rounded-full"
                          style={{
                            width: `${
                              (total / (monthlyStats.expense || 1)) * 100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-300 text-sm py-2">
                    本月暂无数据
                  </p>
                )}
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-10 text-gray-300">还没有记录哦</div>
          ) : (
            filteredTransactions.map((t) => (
              <div
                key={t.id}
                className="bg-white p-4 rounded-2xl shadow-sm flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      t.type === "deposit"
                        ? t.person === "磊磊"
                          ? "bg-blue-100 text-blue-500"
                          : "bg-pink-100 text-pink-500"
                        : "bg-orange-100 text-orange-500"
                    }`}
                  >
                    {t.type === "deposit"
                      ? t.person === "磊磊"
                        ? "👦"
                        : "👧"
                      : CATEGORIES[t.category]?.icon}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm flex items-center gap-2">
                      {t.type === "deposit"
                        ? `${t.person} 存钱`
                        : t.subcategory}
                      {t.type === "expense" && t.person && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            t.person === "磊磊"
                              ? "bg-blue-50 text-blue-500"
                              : "bg-pink-50 text-pink-500"
                          }`}
                        >
                          {t.person}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t.note} {new Date(t.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-bold ${
                      t.type === "deposit" ? "text-green-500" : "text-gray-800"
                    }`}
                  >
                    {t.type === "deposit" ? "+" : "-"} {t.amount}
                  </span>
                  <button onClick={() => handleDelete(t.id)}>
                    <Trash2
                      size={16}
                      className="text-gray-300 hover:text-red-500"
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
